import React, { useState } from "react";
import { useProyectos } from "../hooks/useProyectos";

const GestionProyectos = ({ showToast, activeProjectId, setActiveProjectId }) => {
    const { proyectos, loading, deleteProject, saveProject } = useProyectos(showToast, activeProjectId, setActiveProjectId);
    const [showCreate, setShowCreate] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

    const handleCreateProject = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());

        try {
            await saveProject(data, editingProject);
            showToast(`✅ Proyecto ${editingProject ? 'actualizado' : 'creado'} correctamente.`, "success");
            setShowCreate(false);
            setEditingProject(null);
        } catch (err) {
            console.error("Error al procesar proyecto:", err);
        }
    };

    const handleSelectActive = (id) => {
        setActiveProjectId(id);
        localStorage.setItem("activeProjectId", id);
        showToast("Proyecto científico seleccionado como base activa.", "success");
    };

    const handleDeleteProject = async (id) => {
        try {
            await deleteProject(id);
            showToast("✅ Registro de proyecto eliminado.", "success");
            setShowDeleteConfirm(null);
        } catch (err) {
            showToast("Error al eliminar. Existen datos asociados a este proyecto.", "danger");
            setShowDeleteConfirm(null);
        }
    };

    return (
        <div className="animate__animated animate__fadeIn">
            {/* Cabecera de Módulo */}
            <div className="d-flex justify-content-between align-items-center mb-5">
                <div>
                    <h3 className="fw-bold text-dark m-0">Gestión de Proyectos Científicos</h3>
                    <p className="text-muted small mb-0">Administración de cohortes y parámetros de investigación epidemiológica</p>
                </div>
                <button className="btn btn-success fw-bold px-4 py-2 shadow-sm d-flex align-items-center gap-2" style={{ borderRadius: '12px' }} onClick={() => { setEditingProject(null); setShowCreate(true); }}>
                    <i className="bi bi-folder-plus fs-5"></i> INICIAR NUEVO PROYECTO
                </button>
            </div>

            {/* Modal de Creación/Edición (In-page) */}
            {showCreate && (
                <div className="card border-0 shadow-lg mb-5 animate__animated animate__slideInDown" style={{ borderRadius: '20px', borderLeft: '6px solid var(--geohcx-green-primary)' }}>
                    <div className="card-header bg-white border-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="m-0 fw-bold text-dark">{editingProject ? "Modificar Protocolo de Proyecto" : "Configuración de Nuevo Proyecto de Investigación"}</h5>
                            <p className="text-muted small mb-0">Defina los parámetros básicos de vigilancia y control para la nueva cohorte</p>
                        </div>
                        <button className="btn-close" onClick={() => { setShowCreate(false); setEditingProject(null); }}></button>
                    </div>
                    <div className="card-body p-4 pt-2">
                        <form onSubmit={handleCreateProject} key={editingProject?.id || 'new'}>
                            <div className="row g-4">
                                <div className="col-md-8">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Título Institucional del Proyecto</label>
                                    <input name="nombre" className="form-control form-control-lg border-2 shadow-xs" style={{ borderRadius: '12px' }} required defaultValue={editingProject?.nombre} placeholder="Ej: Vigilancia Epidemiológica de STH en zona centro" />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Código de Protocolo (Único)</label>
                                    <input name="codigo" className="form-control form-control-lg font-monospace text-primary border-2 shadow-xs" style={{ borderRadius: '12px' }} required defaultValue={editingProject?.codigo} placeholder="INVEST-2024-X" />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Investigador Líder</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-2 border-end-0"><i className="bi bi-person-badge"></i></span>
                                        <input name="investigador_principal" className="form-control border-2" style={{ borderRadius: '0 12px 12px 0' }} required defaultValue={editingProject?.investigador_principal} placeholder="Nombre completo" />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Agencia / Sponsor</label>
                                    <input name="financiador" className="form-control border-2" style={{ borderRadius: '12px' }} required defaultValue={editingProject?.financiador} placeholder="Ej: GEOHCX Foundation / MinCiencias" />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Área Geográfica de Cobertura</label>
                                    <input name="lugar" className="form-control border-2" style={{ borderRadius: '12px' }} required defaultValue={editingProject?.lugar} placeholder="Ej: Región Caribe, Colombia" />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Fecha de Lanzamiento</label>
                                    <input name="fecha_inicio" type="date" className="form-control border-2" style={{ borderRadius: '12px' }} required defaultValue={editingProject?.fecha_inicio} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Fecha de Finalización</label>
                                    <input name="fecha_fin" type="date" className="form-control border-2" style={{ borderRadius: '12px' }} required defaultValue={editingProject?.fecha_fin} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Muestra Estimada (N)</label>
                                    <input name="estimado_participantes" type="number" className="form-control border-2" style={{ borderRadius: '12px' }} defaultValue={editingProject?.estimado_participantes || "0"} />
                                </div>
                                <div className="col-12">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Resumen de Objetivos Científicos</label>
                                    <textarea name="objetivo" className="form-control border-2" rows="3" style={{ borderRadius: '12px' }} required defaultValue={editingProject?.objetivo} placeholder="Describa brevemente la finalidad epidemiológica de este proyecto..."></textarea>
                                </div>
                                <div className="col-12 text-end mt-4">
                                    <button type="button" className="btn btn-light me-2 fw-bold px-4" style={{ borderRadius: '10px' }} onClick={() => { setShowCreate(false); setEditingProject(null); }}>CANCELAR</button>
                                    <button type="submit" className="btn btn-success px-5 fw-bold shadow-sm" style={{ borderRadius: '10px' }}>{editingProject ? "ACTUALIZAR PROYECTO" : "FINALIZAR REGISTRO"}</button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Listado de Proyectos */}
            <div className="researcher-table-container">
                <div className="table-responsive">
                    <table className="researcher-table align-middle">
                        <thead>
                            <tr>
                                <th style={{ width: '120px' }}>Estado Operativo</th>
                                <th>Referencia / Código</th>
                                <th>Nombre Institucional</th>
                                <th>Investigador Encargado</th>
                                <th>Cronograma</th>
                                <th className="text-center">Alcance (N)</th>
                                <th className="text-end">Herramientas</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" className="text-center py-5">
                                    <div className="spinner-border text-success spinner-border-sm me-2"></div>
                                    <span className="text-muted fw-bold">Sincronizando biblioteca de proyectos...</span>
                                </td></tr>
                            ) : proyectos.length === 0 ? (
                                <tr><td colSpan="7" className="text-center py-5 text-muted fw-bold">No se han detectado proyectos asociados a su cuenta.</td></tr>
                            ) : (
                                proyectos.map(p => (
                                    <tr key={p.id} className={activeProjectId === p.id ? "bg-success bg-opacity-10" : ""}>
                                        <td className="text-center">
                                            {activeProjectId === p.id ? (
                                                <span className="badge bg-success shadow-sm px-3 py-2 w-100" style={{ borderRadius: '10px' }}>
                                                    <i className="bi bi-activity me-1"></i> EN CURSO
                                                </span>
                                            ) : (
                                                <span className="badge bg-light text-muted border px-3 py-2 w-100" style={{ borderRadius: '10px' }}>ARCHIVADO</span>
                                            )}
                                        </td>
                                        <td className="fw-bold text-primary font-monospace">{p.codigo}</td>
                                        <td>
                                            <div className="fw-bold text-dark">{p.nombre}</div>
                                            <div className="text-muted smaller fw-bold"><i className="bi bi-bank2 me-1"></i>{p.financiador}</div>
                                        </td>
                                        <td>
                                            <div className="small fw-bold">{p.investigador_principal}</div>
                                        </td>
                                        <td className="small">
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <i className="bi bi-calendar-check text-success"></i>
                                                <span className="text-muted font-monospace">{p.fecha_inicio}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <i className="bi bi-calendar-x text-danger"></i>
                                                <span className="text-muted font-monospace">{p.fecha_fin}</span>
                                            </div>
                                        </td>
                                        <td className="text-center fw-bold fs-5 text-dark">{p.estimado_participantes}</td>
                                        <td className="text-end">
                                            <div className="d-flex justify-content-end gap-1">
                                                {activeProjectId !== p.id && (
                                                    <button className="btn btn-sm btn-success fw-bold px-3" style={{ borderRadius: '8px' }} onClick={() => handleSelectActive(p.id)}>
                                                        ACTIVAR
                                                    </button>
                                                )}
                                                <button className="btn btn-sm btn-light text-success border" style={{ borderRadius: '8px' }} onClick={() => { setEditingProject(p); setShowCreate(true); }}>
                                                    <i className="bi bi-pencil-square"></i>
                                                </button>
                                                <button className="btn btn-sm btn-light text-danger border" style={{ borderRadius: '8px' }} onClick={() => setShowDeleteConfirm(p)}>
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {showDeleteConfirm && (
                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 100, background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(4px)', borderRadius: '20px' }}>
                        <div className="card border-0 shadow-lg" style={{ maxWidth: '450px', borderRadius: '20px' }}>
                            <div className="card-body text-center p-4">
                                <div className="bg-danger bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                                    <i className="bi bi-shield-slash text-danger fs-1"></i>
                                </div>
                                <h5 className="fw-bold text-dark">¿Terminar Proyecto?</h5>
                                <div className="bg-light p-3 rounded-3 mb-3 text-start small border border-danger border-opacity-25">
                                    <div className="fw-bold text-danger mb-1"><i className="bi bi-exclamation-triangle-fill me-2"></i>ALERTA DE SEGURIDAD</div>
                                    <div className="text-dark fw-bold">{showDeleteConfirm.nombre}</div>
                                    <div className="text-muted font-monospace">{showDeleteConfirm.codigo}</div>
                                </div>
                                <p className="text-muted small">
                                    Esta acción eliminará permanentemente la configuración del proyecto y desvinculará todos los casos clínicos asociados. Esta operación es crítica.
                                </p>
                                <div className="d-flex justify-content-center gap-2 mt-4">
                                    <button className="btn btn-light px-4 fw-bold" style={{ borderRadius: '10px' }} onClick={() => setShowDeleteConfirm(null)}>ABORTAR</button>
                                    <button className="btn btn-danger px-4 fw-bold shadow-sm" style={{ borderRadius: '10px' }} onClick={() => handleDeleteProject(showDeleteConfirm.id)}>SÍ, ELIMINAR PROYECTO</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GestionProyectos;

